/**
 * MoonPhase — an SVG moon that draws the TRUE illuminated fraction, not one of a
 * handful of bucketed emoji glyphs. The lit limb is bounded by the disc edge on the
 * lit side and the terminator ellipse on the inside, so every tithi gets its own
 * shape: a thin sliver at Pratipadā filling to a full disc at Pūrṇimā, then emptying
 * from the other side through the waning half.
 *
 * Direction follows pakṣa: waxing → light grows on the RIGHT, waning → light shrinks
 * on the LEFT (northern-hemisphere convention, matching how the sky rail reads it).
 *
 * Geometry: for lit fraction p∈[0,1] the terminator is an ellipse with x-radius
 * R·|1−2p| and y-radius R. The lit region is one semicircle (the lit side) closed by
 * that terminator arc. p<0.5 ⇒ crescent (terminator bulges toward the dark side);
 * p>0.5 ⇒ gibbous (terminator bulges toward the lit side); p=0.5 ⇒ straight edge.
 */

import React from 'react';

export interface MoonPhaseProps {
  /** Illuminated fraction as a percentage, 0–100. */
  illumination: number;
  /** True while waxing (Śukla pakṣa) → lit on the right; false → waning, lit on the left. */
  waxing: boolean;
  /** Rendered diameter in px. */
  size?: number;
  /** Lit-limb fill. */
  litColor?: string;
  /** Unlit-body fill. */
  darkColor?: string;
  /** Disc outline (keeps a new moon readable as a circle). */
  strokeColor?: string;
  className?: string;
  /** Accessible label; defaults to a phase description. */
  title?: string;
}

/** Path for the lit region of a unit disc centred at (0,0), radius R. */
export function litLimbPath(p: number, waxing: boolean, R: number): string {
  const frac = Math.max(0, Math.min(1, p));
  const rx = R * Math.abs(1 - 2 * frac);
  // sweep=1 traces the right semicircle (through +x); =0 traces the left.
  const semiSweep = waxing ? 1 : 0;
  // Gibbous terminator bulges the same way as the lit semicircle; crescent flips it.
  const termSweep = frac >= 0.5 ? semiSweep : 1 - semiSweep;
  return (
    `M 0 ${-R} ` +
    `A ${R} ${R} 0 0 ${semiSweep} 0 ${R} ` +
    `A ${rx} ${R} 0 0 ${termSweep} 0 ${-R} Z`
  );
}

export const MoonPhase: React.FC<MoonPhaseProps> = ({
  illumination,
  waxing,
  size = 14,
  litColor = '#E2E8F0',
  darkColor = '#1E293B',
  strokeColor = '#94A3B8',
  className,
  title,
}) => {
  const p = Math.max(0, Math.min(1, illumination / 100));
  const R = 10; // viewBox unit radius; scaled by `size`
  const stroke = 1;
  const label = title ?? `${waxing ? 'Waxing' : 'Waning'} moon, ${Math.round(illumination)}% lit`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${-R - stroke} ${-R - stroke} ${(R + stroke) * 2} ${(R + stroke) * 2}`}
      className={className}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {/* Unlit body */}
      <circle cx={0} cy={0} r={R} fill={darkColor} />
      {/* Lit limb — the exact illuminated fraction */}
      {p > 0.003 && <path d={litLimbPath(p, waxing, R)} fill={litColor} />}
      {/* Outline keeps the disc visible at new moon */}
      <circle cx={0} cy={0} r={R} fill="none" stroke={strokeColor} strokeWidth={stroke} opacity={0.85} />
    </svg>
  );
};

export default MoonPhase;
